import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useRecoilValue } from 'recoil';
import * as api from '../../services/api';
import useConvertTimeRange from '../useConvertTimeRange';
import useToaster from '../useToaster';
import * as sharedAtoms from '../../atoms/shared';
import useAccount from './useAccount';

export const TIMESHEET_PAYROLL_FIELDS_QUERY_KEY = 'timesheet-payroll-fields';

const useTimesheetPayrollFields = ({ timeRange, projectId, branchId, employeeIds, enabled = true }) => {
  const { accountId } = useAccount();
  const selectedBranches = useRecoilValue(sharedAtoms.selectedBranches);
  const { addResponseError } = useToaster();
  const queryClient = useQueryClient();

  const timeRangeValue = useConvertTimeRange(timeRange);

  const branches = useMemo(() => (branchId ? [branchId] : selectedBranches || []), [branchId, selectedBranches]);

  const projectIds = useMemo(() => (projectId ? [projectId] : undefined), [projectId]);

  const hasRange = !!projectId || (!!timeRangeValue.from && !!timeRangeValue.to);

  const storeKey = useMemo(
    () => [TIMESHEET_PAYROLL_FIELDS_QUERY_KEY, accountId, branches, timeRangeValue, projectIds, employeeIds],
    [accountId, branches, timeRangeValue, projectIds, employeeIds]
  );

  const { data, isLoading, refetch } = useQuery(
    storeKey,
    () =>
      api.fetchTimesheetPayrollFields({
        accountId,
        branches,
        ...(projectId ? {} : timeRangeValue),
        projectIds,
        employeeIds
      }),
    {
      enabled: enabled && !!accountId && branches.length > 0 && hasRange,
      select: (response) => response?.data
    }
  );

  const fields = useMemo(() => (data?.fields || []).filter((field) => !!field?.id), [data?.fields]);

  const mapByTimesheetId = useMemo(
    () =>
      (data?.rows || []).reduce((output, row) => {
        if (row?.timesheetId) {
          output[row.timesheetId] = row;
        }
        return output;
      }, {}),
    [data?.rows]
  );

  const updateField = useMutation(
    ({ employeeSlotId, jobId, payrollFieldId, value }) => {
      if (employeeSlotId) {
        return api.updateTimesheetSlotPayrollField({ accountId, employeeSlotId, payrollFieldId, value });
      }

      if (jobId) {
        return api.updateTimesheetJobPayrollField({ accountId, jobId, payrollFieldId, value });
      }

      return Promise.resolve();
    },
    {
      onSuccess: () => queryClient.invalidateQueries(storeKey),
      onError: (error) => addResponseError(error)
    }
  );

  return { fields, mapByTimesheetId, isLoading, refetch, updateField };
};

export default useTimesheetPayrollFields;
